import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import "./ServicesPage.css";
import { getConsultationPath } from "../../utils/consultationLink";
import { WEB_LEAD_MODULES } from "../../constants/webLeadModules";
import Navbar from "../../components/Navbar/Navbar";
import Footer from "../../components/Footer/Footer";

/* ── Scroll-reveal hook ── */
const useInView = (threshold = 0.15) => {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) {
          setInView(true);
          obs.disconnect();
        }
      },
      { threshold },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, inView];
};

/* ════════════════════════════════════════════════════
   ISOMETRIC SCENES — 4 hero scenes
════════════════════════════════════════════════════ */

/* Scene 1 — Wealth Growth */
const WealthGrowthScene = () => (
  <div className="wcs-scene-wrapper wcs-scene-fade-in">
    {/* Top-left: AUM badge */}
    <div className="wcs-badge-pill wcs-pill-tl">
      <i className="fas fa-chart-pie" />
      <span>₹100cr+ AUM</span>
    </div>

    {/* Top-right: spinning coin */}
    <div className="wcs-coin wcs-coin-tr">
      <div className="wcs-coin-face wcs-coin-front">₹</div>
      <div className="wcs-coin-face wcs-coin-back" />
    </div>

    {/* Main dashboard card */}
    <div className="wcs-main-card wcs-card-white">
      <div className="wcs-card-titlebar">
        <div className="wcs-dots">
          <span />
          <span />
          <span />
        </div>
        <span className="wcs-card-label">Portfolio Overview</span>
        <span className="wcs-chip wcs-chip-green">+57% Growth</span>
      </div>
      <div className="wcs-chart-area">
        <div className="wcs-chart-ylabels">
          <span>₹80L</span>
          <span>₹60L</span>
          <span>₹40L</span>
          <span>₹20L</span>
          <span>₹0</span>
        </div>
        <div className="wcs-chart-body">
          <svg
            viewBox="0 0 260 120"
            className="wcs-chart-svg"
            preserveAspectRatio="none"
          >
            <defs>
              <linearGradient id="wcs-area-grad2" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#2563eb" stopOpacity="0.25" />
                <stop offset="100%" stopColor="#2563eb" stopOpacity="0.02" />
              </linearGradient>
              <linearGradient id="wcs-line-grad2" x1="0" y1="0" x2="1" y2="0">
                <stop offset="0%" stopColor="#2563eb" />
                <stop offset="100%" stopColor="#10b981" />
              </linearGradient>
            </defs>
            {[20, 45, 70, 95].map((y, i) => (
              <line
                key={i}
                x1="0"
                y1={y}
                x2="260"
                y2={y}
                stroke="rgba(37,99,235,0.08)"
                strokeWidth="1"
              />
            ))}
            <path
              d="M0,115 C30,108 50,100 70,90 C90,80 110,70 130,58 C150,46 170,36 195,24 C215,15 240,10 260,6 L260,120 L0,120 Z"
              fill="url(#wcs-area-grad2)"
              className="wcs-area-fill"
            />
            <path
              d="M0,115 C30,108 50,100 70,90 C90,80 110,70 130,58 C150,46 170,36 195,24 C215,15 240,10 260,6"
              fill="none"
              stroke="url(#wcs-line-grad2)"
              strokeWidth="3"
              strokeLinecap="round"
              className="wcs-line-path"
            />
            {[
              [0, 115],
              [70, 90],
              [130, 58],
              [195, 24],
              [260, 6],
            ].map(([cx, cy], i) => (
              <circle
                key={i}
                cx={cx}
                cy={cy}
                r="4"
                fill="white"
                stroke="#2563eb"
                strokeWidth="2"
                className="wcs-dot-pop"
                style={{ animationDelay: `${i * 0.15 + 1}s` }}
              />
            ))}
          </svg>
          <div className="wcs-chart-xlabels">
            <span>Jan</span>
            <span>Mar</span>
            <span>Jun</span>
            <span>Sep</span>
            <span>Dec</span>
          </div>
        </div>
      </div>
    </div>

    {/* Bottom-left: money stack */}
    <div className="wcs-stack wcs-stack-bl">
      <div className="wcs-bill wcs-bill-1">₹</div>
      <div className="wcs-bill wcs-bill-2">₹</div>
      <div className="wcs-bill wcs-bill-3">₹</div>
    </div>

    {/* Bottom-right: investors badge */}
    <div className="wcs-badge-pill wcs-pill-br">
      <i className="fas fa-users" />
      <span>800+ Investors</span>
    </div>

    {/* Shield orb */}
    <div className="wcs-orb wcs-orb-blue wcs-orb-ml">
      <i className="fas fa-shield-halved" />
    </div>
  </div>
);

/* Scene 2 — Expert Consultation */
const ConsultationScene = () => (
  <div className="wcs-scene-wrapper wcs-scene-fade-in">
    {/* Top-left: Advisor person */}
    <div className="wcs-person-card wcs-person-tl">
      <div className="wcs-person-avatar wcs-avatar-blue">
        <i className="fas fa-user-tie" />
      </div>
      <div className="wcs-person-body wcs-body-blue" />
      <span className="wcs-person-label">Advisor</span>
    </div>

    {/* Top-right: Plan float card */}
    <div className="wcs-float-card wcs-fc-tr">
      <i className="fas fa-clipboard-check" />
      <span>Plan</span>
    </div>

    {/* Main card */}
    <div className="wcs-main-card wcs-card-dark-gradient">
      <div className="wcs-consult-icon-wrap">
        <i className="fas fa-handshake" />
      </div>
      <div className="wcs-consult-title">Expert Consultation</div>
      <div className="wcs-consult-meta">
        <div className="wcs-meta-item">
          <i className="fas fa-check-circle" /> Portfolio Analysis
        </div>
        <div className="wcs-meta-item">
          <i className="fas fa-check-circle" /> Risk Assessment
        </div>
        <div className="wcs-meta-item">
          <i className="fas fa-check-circle" /> Growth Strategy
        </div>
      </div>
    </div>

    {/* Right: document */}
    <div className="wcs-doc-card wcs-doc-mr">
      <i className="fas fa-file-contract" />
    </div>

    {/* Bottom-left: Schedule */}
    <div className="wcs-float-card wcs-fc-bl">
      <i className="fas fa-calendar-alt" />
      <span>Schedule</span>
    </div>

    {/* Bottom-centre: chart doc */}
    <div className="wcs-doc-card wcs-doc-bm">
      <i className="fas fa-chart-bar" />
    </div>

    {/* Bottom-right: Client person */}
    <div className="wcs-person-card wcs-person-br">
      <div className="wcs-person-avatar wcs-avatar-teal">
        <i className="fas fa-user" />
      </div>
      <div className="wcs-person-body wcs-body-teal" />
      <span className="wcs-person-label">Client</span>
    </div>
  </div>
);

/* Scene 3 — Live Trading */
const TradingScene = () => (
  <div className="wcs-scene-wrapper wcs-scene-fade-in">
    {/* Top-left: ticker stack */}
    <div className="wcs-ticker-stack">
      <div className="wcs-ticker-card">
        <span className="wcs-ticker-sym">NIFTY</span>
        <span className="wcs-ticker-val wcs-val-up">+2.4%</span>
      </div>
      <div className="wcs-ticker-card" style={{ animationDelay: "0.15s" }}>
        <span className="wcs-ticker-sym">SENSEX</span>
        <span className="wcs-ticker-val wcs-val-up">+1.8%</span>
      </div>
    </div>

    {/* Top-right: trend arrow orb */}
    <div className="wcs-orb wcs-orb-green wcs-orb-tr">
      <i className="fas fa-arrow-trend-up" />
    </div>

    {/* Main trading terminal */}
    <div className="wcs-main-card wcs-card-dark wcs-trading-main">
      <div className="wcs-terminal-header">
        <span className="wcs-terminal-title">LIVE MARKET</span>
        <span className="wcs-terminal-status">
          <span className="wcs-status-dot-active" /> Active
        </span>
      </div>
      <div className="wcs-candlestick-chart">
        {[
          { h: "42%", green: true, d: 0.1 },
          { h: "28%", green: false, d: 0.2 },
          { h: "52%", green: true, d: 0.3 },
          { h: "65%", green: true, d: 0.4 },
          { h: "38%", green: false, d: 0.5 },
          { h: "72%", green: true, d: 0.6 },
          { h: "80%", green: true, d: 0.7 },
        ].map((c, i) => (
          <div key={i} className="wcs-candle-wrap">
            <div
              className={`wcs-candle ${c.green ? "wcs-c-green" : "wcs-c-red"}`}
              style={{ height: c.h, animationDelay: `${c.d}s` }}
            />
          </div>
        ))}
      </div>
    </div>

    {/* Bottom-left: volume bars */}
    <div className="wcs-volume-group">
      {["35%", "55%", "75%", "95%"].map((h, i) => (
        <div
          key={i}
          className="wcs-vol-bar"
          style={{ height: h, animationDelay: `${i * 0.1}s` }}
        />
      ))}
    </div>

    {/* Bottom-right: coin */}
    <div className="wcs-coin wcs-coin-br">
      <div className="wcs-coin-face wcs-coin-front">₹</div>
      <div className="wcs-coin-face wcs-coin-back" />
    </div>
  </div>
);

/* Scene 4 — Loan Against Security */
const LoanSecurityScene = () => (
  <div className="wcs-scene-wrapper wcs-scene-fade-in">
    {/* Top-left: doc stack */}
    <div className="wcs-doc-stack wcs-doc-tl">
      <div className="wcs-sec-doc">
        <i className="fas fa-file-invoice" />
        <span>Securities</span>
      </div>
      <div className="wcs-sec-doc" style={{ animationDelay: "0.3s" }}>
        <i className="fas fa-certificate" />
        <span>Bonds</span>
      </div>
    </div>

    {/* Top-right: credit card */}
    <div className="wcs-credit-card wcs-cc-tr">
      <div className="wcs-cc-chip" />
      <div className="wcs-cc-number">•••• •••• •••• 1234</div>
    </div>

    {/* Main card */}
    <div className="wcs-main-card wcs-card-dark-blue">
      <div className="wcs-loan-header">
        <i className="fas fa-building-columns" />
        <span>Loan Against Securities</span>
      </div>
      <div className="wcs-loan-credit-box">
        <span className="wcs-credit-lbl">Available Credit</span>
        <span className="wcs-credit-val">₹50 Lakhs</span>
      </div>
      <div className="wcs-loan-meta">
        <div className="wcs-loan-row">
          <span>Interest Rate</span>
          <span className="wcs-loan-highlight">8.5% p.a.</span>
        </div>
        <div className="wcs-loan-row">
          <span>Approval Time</span>
          <span className="wcs-loan-highlight">24 Hours</span>
        </div>
      </div>
    </div>

    {/* Bottom-left: approved stamp */}
    <div className="wcs-stamp wcs-stamp-bl">
      <i className="fas fa-circle-check" />
      <span>APPROVED</span>
    </div>

    {/* Bottom-right: money stack */}
    <div className="wcs-stack wcs-stack-br">
      <div className="wcs-bill wcs-bill-1">₹</div>
      <div className="wcs-bill wcs-bill-2">₹</div>
      <div className="wcs-bill wcs-bill-3">₹</div>
    </div>

    {/* Lock orb */}
    <div className="wcs-orb wcs-orb-green wcs-orb-mr">
      <i className="fas fa-lock" />
    </div>
  </div>
);

/* ─── Scene meta ─── */
const SCENES = [
  {
    id: 0,
    label: "Growth",
    icon: "fa-chart-line",
    Component: WealthGrowthScene,
  },
  {
    id: 1,
    label: "Consultation",
    icon: "fa-handshake",
    Component: ConsultationScene,
  },
  {
    id: 2,
    label: "Trading",
    icon: "fa-arrow-trend-up",
    Component: TradingScene,
  },
  {
    id: 3,
    label: "Loan",
    icon: "fa-building-columns",
    Component: LoanSecurityScene,
  },
];

/* ════════════════════════════════════════════════════
   DATA
════════════════════════════════════════════════════ */
const WHY_US = [
  {
    icon: "fa-magnifying-glass-chart",
    title: "Structured Investment Approach",
    desc: "Our approach is built on disciplined investing, long-term thinking, and well-defined processes — not short-term market noise.",
    color: "#2563eb",
  },
  {
    icon: "fa-handshake",
    title: "Transparent & Client-Focused",
    desc: "No hidden charges, complete clarity, and a process designed to keep your interests first at every step.",
    color: "#0891b2",
  },
  {
    icon: "fa-user-tie",
    title: "Goal-Oriented Investing",
    desc: "Investment options are aligned with your financial goals, time horizon, and preferences to support better decision-making.",
    color: "#7c3aed",
  },
  {
    icon: "fa-seedling",
    title: "Long-Term Wealth Focus",
    desc: "We focus on consistency and compounding, helping you stay invested with a long-term perspective.",
    color: "#10b981",
  },
  {
    icon: "fa-chart-line",
    title: "Ongoing Tracking & Updates",
    desc: "Stay informed with regular updates and performance tracking through structured reporting.",
    color: "#f59e0b",
  },
];

const SERVICES = [
  // {
  //   icon: "fa-briefcase",
  //   title: "Portfolio Management",
  //   desc: "End-to-end management of your equity, debt, and hybrid portfolios with regular rebalancing and performance reporting.",
  //   tags: ["Equity", "Debt", "Hybrid"],
  //   color: "#2563eb",
  //   colorEnd: "#60a5fa",
  // },
  {
    icon: "fa-chart-pie",
    title: "Wealth Planning",
    desc: "Comprehensive financial roadmaps that map your life goals to investment timelines and asset allocation strategies.",
    tags: ["Goal-Based", "Asset Allocation"],
    color: "#0891b2",
    colorEnd: "#38bdf8",
  },
  {
    icon: "fa-umbrella",
    title: "Retirement Planning",
    desc: "Build a retirement corpus that sustains your lifestyle — through systematic SIPs, NPS optimisation, and income planning.",
    tags: ["SIP", "NPS", "Income Planning"],
    color: "#7c3aed",
    colorEnd: "#a78bfa",
  },
  {
    icon: "fa-building-columns",
    title: "Loan Against Securities",
    desc: "Unlock liquidity from your existing investments without selling them. Get credit at competitive rates against your portfolio.",
    tags: ["Liquidity", "Competitive Rates"],
    color: "#10b981",
    colorEnd: "#6ee7b7",
  },
  {
    icon: "fa-file-invoice-dollar",
    title: "Tax-Efficient Investing",
    desc: "Legally optimise your investment tax liability through ELSS, harvesting strategies, and smart holding-period planning.",
    tags: ["ELSS", "Tax Harvesting"],
    color: "#f59e0b",
    colorEnd: "#fcd34d",
  },
  // {
  //   icon: "fa-shield-halved",
  //   title: "Risk Management Planning",
  //   desc: "Identify, measure, and mitigate portfolio risks through diversification, hedging strategies, and stress-testing.",
  //   tags: ["Diversification", "Hedging"],
  //   color: "#ef4444",
  //   colorEnd: "#fca5a5",
  // },
];


/* ════════════════════════════════════════════════════
   SUB-COMPONENTS
════════════════════════════════════════════════════ */
const ServiceCard = ({ service, delay, inView }) => {
  const navigate = useNavigate();
  const [hovered, setHovered] = useState(false);
  return (
    <div
      className={`wcs-service-card ${inView ? "wcs-in-view" : ""}`}
      style={{ transitionDelay: `${delay}ms` }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <div
        className="wcs-card-top-bar"
        style={{
          background: `linear-gradient(90deg, ${service.color}, ${service.colorEnd})`,
        }}
      />
      <div
        className="wcs-card-icon-wrap"
        style={{
          background: hovered ? `${service.color}20` : `${service.color}12`,
          border: `1.5px solid ${service.color}${hovered ? "40" : "22"}`,
        }}
      >
        <i className={`fas ${service.icon}`} style={{ color: service.color }} />
      </div>
      <h3 className="wcs-card-title">{service.title}</h3>
      <p className="wcs-card-desc">{service.desc}</p>
      <div className="wcs-card-tags">
        {service.tags.map((t) => (
          <span
            key={t}
            className="wcs-card-tag"
            style={{
              color: service.color,
              background: `${service.color}10`,
              border: `1px solid ${service.color}25`,
            }}
          >
            {t}
          </span>
        ))}
      </div>
      <button
        className="wcs-card-cta"
        style={{
          color: hovered ? "#fff" : service.color,
          borderColor: service.color,
          background: hovered ? service.color : "transparent",
        }}
        onClick={() => navigate(getConsultationPath(service.title))}
      >
        Learn More <i className="fas fa-arrow-right" />
      </button>
      <div
        className="wcs-card-glow"
        style={{
          background: `radial-gradient(ellipse 80% 40% at 50% 0%, ${service.color}14, transparent 70%)`,
        }}
      />
    </div>
  );
};

const WhyCard = ({ item, index, inView }) => (
  <div
    className={`wcs-why-card ${inView ? "wcs-in-view" : ""}`}
    style={{ transitionDelay: `${index * 90}ms` }}
  >
    <div className="wcs-why-number">{String(index + 1).padStart(2, "0")}</div>
    <div
      className="wcs-why-icon-wrap"
      style={{ background: `${item.color}15`, color: item.color }}
    >
      <i className={`fas ${item.icon}`} />
    </div>
    <div className="wcs-why-body">
      <h3>{item.title}</h3>
      <p>{item.desc}</p>
    </div>
    <div className="wcs-why-accent" style={{ background: item.color }} />
  </div>
);


/* ════════════════════════════════════════════════════
   PAGE
════════════════════════════════════════════════════ */
const ServicesPage = () => {
  const navigate = useNavigate();
  const [currentScene, setCurrentScene] = useState(0);
  const [heroRef, heroInView] = useInView(0.1);
  const [whyRef, whyInView] = useInView(0.08);
  const [servicesRef, servicesInView] = useInView(0.06);

  /* Auto-rotate every 5s */
  useEffect(() => {
    const t = setInterval(
      () => setCurrentScene((p) => (p + 1) % SCENES.length),
      5000,
    );
    return () => clearInterval(t);
  }, []);

  const ActiveScene = SCENES[currentScene].Component;

  useEffect(() => {
  window.scrollTo(0, 0);
}, []);

  return (
    <div className="wcs-wrapper">
      <Navbar />
      {/* ══════════════════════════════════════════
          SECTION 2 — SERVICES GRID
      ══════════════════════════════════════════ */}
      <section className="wcs-services" id="wcs-services" ref={servicesRef}>
        <div className="wcs-services-bg" />
        <div className="wcs-services-blob-1" />
        <div className="wcs-services-blob-2" />
        <div className="wcs-container">
          <div className="wcs-section-header">
            <div className="wcs-pill wcs-pill-blue">
              <i className="fas fa-star" /> What We Offer
            </div>
            <h2 className="wcs-section-title">
              Services We <span className="wcs-gradient-text">Provide</span>
            </h2>
            <p className="wcs-section-body">
              Every service is designed around one goal: growing and protecting
              your wealth with the discipline of a long-term investor.
            </p>
          </div>
          <div className="wcs-services-grid">
            {SERVICES.map((s, i) => (
              <ServiceCard
                key={s.title}
                service={s}
                delay={i * 80}
                inView={servicesInView}
              />
            ))}
          </div>
          <div
            className={`wcs-services-cta ${servicesInView ? "wcs-in-view" : ""}`}
            style={{ transitionDelay: "560ms" }}
          >
            <div className="wcs-services-cta-text">
              <h3>Not sure which service is right for you?</h3>
              <p>
                Schedule a free consultation and our advisors will map the
                perfect strategy to your goals.
              </p>
            </div>
            <button
              className="wcs-btn-primary"
              onClick={() => navigate(getConsultationPath(WEB_LEAD_MODULES.SERVICES))}
            >
              Get Personalised Advice <i className="fas fa-arrow-right" />
            </button>
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════
          SECTION 1 — WHY TRUST US
      ══════════════════════════════════════════ */}
      <section className="wcs-why" id="wcs-why" ref={whyRef}>
        <div className="wcs-why-glow" />
        <div className="wcs-container">
          <div className="wcs-section-header">
            <div className="wcs-pill wcs-pill-blue">
              <i className="fas fa-trophy" /> Why Choose Us
            </div>
            <h2 className="wcs-section-title">
              Why Investors <span className="wcs-gradient-text">Trust Us</span>
            </h2>
            <p className="wcs-section-body">
              We've earned the trust of 800+ investors not through marketing,
              but through consistent results, honest advice, and an unwavering
              commitment to client success.
            </p>
          </div>
          <div className="wcs-why-grid">
            {WHY_US.map((item, i) => (
              <WhyCard
                key={item.title}
                item={item}
                index={i}
                inView={whyInView}
              />
            ))}
          </div>
        </div>
      </section>
      
      {/* ══════════════════════════════════════════
          HERO — two-col: text left, iso scene right
      ══════════════════════════════════════════ */}
      <section className="wcs-hero" ref={heroRef}>
        <div className="wcs-hero-bg" />
        <div className="wcs-hero-dotgrid" />
        <div className="wcs-hero-glow wcs-glow-l" />
        <div className="wcs-hero-glow wcs-glow-r" />

        <div className="wcs-container wcs-hero-inner">
          {/* Left: text */}
          <div className={`wcs-hero-text ${heroInView ? "wcs-in-view" : ""}`}>
            {/* <div className="wcs-eyebrow">
              <i className="fas fa-layer-group" /> Our Services
            </div> */}
            <h1 className="wcs-hero-title">
              Everything You Need to
              <br />
              <span className="wcs-gradient-text">Build Wealth</span>
            </h1>
            <p className="wcs-hero-body">
              Access a wide range of investment products—from mutual funds to market-linked instruments—through a seamless and transparent platform built for long-term wealth creation.
            </p>
        
            <div className="wcs-hero-ctas">
              <button
                className="wcs-btn-primary"
                onClick={() => navigate(getConsultationPath(WEB_LEAD_MODULES.SERVICES))}
              >
                Explore Investment Options <i className="fas fa-arrow-right" />
              </button>
              {/* <button className="wcs-btn-ghost" onClick={() => navigate(-1)}>
                <i className="fas fa-arrow-left" /> Back
              </button> */}
            </div>

            {/* Stat strip */}
            <div className="wcs-hero-stats">
              {[
                { val: "Seamless", label: "onboarding" },
                { val: "Trusted", label: "financial partners" },
                { val: "Transparent", label: "execution" },
                // { val: "₹100cr+", label: "Assets Under Advisory" },
              ].map((s) => (
                <div className="wcs-hero-stat" key={s.label}>
                  <span className="wcs-hero-stat-val">{s.val}</span>
                  <span className="wcs-hero-stat-lbl">{s.label}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Right: isometric scene */}
          <div
            className={`wcs-hero-visual ${heroInView ? "wcs-in-view wcs-visual-delay" : ""}`}
          >
            <div className="wcs-iso-scene">
              <ActiveScene key={currentScene} />
            </div>

            {/* Scene selector tabs */}
            <div className="wcs-scene-tabs">
              {SCENES.map((s) => (
                <button
                  key={s.id}
                  className={`wcs-scene-tab ${currentScene === s.id ? "wcs-tab-active" : ""}`}
                  onClick={() => setCurrentScene(s.id)}
                  title={s.label}
                >
                  <i className={`fas ${s.icon}`} />
                  <span>{s.label}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>




      <Footer />
    </div>
  );
};

export default ServicesPage;
